import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Individual from "marketplace/individuals/individual";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import Team from "marketplace/teams/team";

const MainContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(1),
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  margin: theme.spacing(1, 0),
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));
const TeamList = styled(List)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    "&:not(:last-of-type)": {
      marginRight: theme.spacing(5),
    },
  },
  margin: 0,
  marginTop: 0,
  minWidth: theme.spacing(40),
  paddingBottom: theme.spacing(3),
}));
const TeamListSubheader = styled(ListSubheader)(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: "1.3em",
  padding: 0,
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.3em",
  paddingTop: theme.spacing(1),
}));
const TeamMemberLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  cursor: "pointer",
  textDecoration: "underline",
}));

type ViewTeamProps = {
  className?: string;
  team?: Team;
};

export default function ViewTeam(props: Readonly<ViewTeamProps>) {
  const { className, team } = props;

  const { openDialog } = useDialog();

  return (
    <MainContainer className={className}>
      <TeamContainer>
        <TeamList>
          <TeamListSubheader>Team Leader</TeamListSubheader>
          <ListItem disableGutters>
            {!team?.leader && <NoRowsPlaceholder>None</NoRowsPlaceholder>}
            {team?.leader && (
              <>
                <ListItemAvatar>
                  <IndividualAvatar
                    individual={team.leader}
                  />
                </ListItemAvatar>
                <ListItemText>
                  <Typography>
                    <TeamMemberLink
                      onClick={() =>
                        openDialog({
                          component: (
                            <ViewIndividualProfile
                              individualId={team.leader.id}
                            />
                          ),
                          titleStyle: {
                            paddingBottom: 0
                          },
                          contentSxProps: {
                            display: "flex",
                            overflowX: "hidden",
                          },
                          MuiProps: {
                            maxWidth: "lg",
                            fullWidth: true,
                          },
                        })
                      }
                    >
                      {team.leader.fullName}
                    </TeamMemberLink>
                  </Typography>
                </ListItemText>
              </>
            )}
          </ListItem>
        </TeamList>
        <TeamList>
          <TeamListSubheader>Team Members</TeamListSubheader>
          {(!team?.members || team?.members.length < 1) && (
            <ListItem disableGutters>
              <NoRowsPlaceholder>None</NoRowsPlaceholder>
            </ListItem>
          )}
          {team?.members?.map((member: Individual) => (
            <ListItem key={member.userId?.value} disableGutters>
              <ListItemAvatar>
                <IndividualAvatar
                  individual={member}
                />
              </ListItemAvatar>
              <ListItemText>
                <Typography>
                  <TeamMemberLink
                    onClick={() =>
                      openDialog({
                        component: (
                          <ViewIndividualProfile
                            individualId={member.id}
                          />
                        ),
                        titleStyle: {
                          paddingBottom: 0
                        },
                        contentSxProps: {
                          display: "flex",
                          overflowX: "hidden",
                        },
                        MuiProps: {
                          maxWidth: "lg",
                          fullWidth: true,
                        },
                      })
                    }
                  >
                    {member.fullName}
                  </TeamMemberLink>
                </Typography>
              </ListItemText>
            </ListItem>
          ))}
        </TeamList>
      </TeamContainer>
    </MainContainer>
  );
}
